/**
 * Personnalisation des listes d'une page « Mes contacts » (migration 0251) :
 * bouton d'en-tête + panneau (activer/masquer, monter/descendre, réinitialiser).
 * Purement présentationnel — l'état et la persistance vivent dans
 * `useContactLayout` (`@/lib/contact-layout`), partagé par les pages tuteur et élève.
 *
 * Mobile-first : chaque contrôle fait au moins 44 × 44 px, pas de glisser-déposer
 * (le repo n'a aucune dépendance dnd) — flèches Monter / Descendre.
 */

import { ChevronDown, ChevronUp, SlidersHorizontal } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import type { ResolvedSection } from '@/lib/contact-layout';

export function ContactLayoutButton({ open, onClick }: { open: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Personnaliser mes listes"
      aria-expanded={open}
      className={cn(
        'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border transition-all active:scale-[0.98]',
        open
          ? 'border-primary-500 bg-primary-50 text-primary-700'
          : 'border-ink-200 text-ink-600 hover:border-primary-300 bg-white',
      )}
    >
      <SlidersHorizontal className="h-4 w-4" />
    </button>
  );
}

interface ContactLayoutPanelProps {
  sections: readonly ResolvedSection[];
  /** Une disposition est enregistrée : « Réinitialiser » est proposé. */
  isCustomized: boolean;
  /** Bloque les contrôles pendant un enregistrement. */
  disabled?: boolean;
  onToggle: (id: string, enabled: boolean) => void;
  onMove: (id: string, direction: 'up' | 'down') => void;
  onReset: () => void;
}

export function ContactLayoutPanel({
  sections,
  isCustomized,
  disabled = false,
  onToggle,
  onMove,
  onReset,
}: ContactLayoutPanelProps) {
  return (
    <div className="border-ink-200 space-y-3 rounded-2xl border bg-white p-4">
      <div>
        <p className="text-ink-900 text-sm font-semibold">Personnaliser mes listes</p>
        <p className="text-ink-500 mt-0.5 font-sans text-xs">
          Affiche ou masque chaque liste et choisis leur ordre. Une liste que tu actives passe tout
          en haut.
        </p>
      </div>

      <ul className="divide-ink-100 divide-y">
        {sections.map((section, index) => (
          <li key={section.id} className="flex items-center gap-1">
            <span
              className={cn(
                'min-w-0 flex-1 py-2 text-sm font-medium',
                section.enabled ? 'text-ink-900' : 'text-ink-400',
              )}
            >
              {section.label}
            </span>

            <button
              type="button"
              onClick={() => onMove(section.id, 'up')}
              disabled={disabled || index === 0}
              aria-label={`Monter « ${section.label} »`}
              className="text-ink-500 hover:text-ink-900 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg transition-colors active:scale-[0.98] disabled:opacity-30"
            >
              <ChevronUp className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => onMove(section.id, 'down')}
              disabled={disabled || index === sections.length - 1}
              aria-label={`Descendre « ${section.label} »`}
              className="text-ink-500 hover:text-ink-900 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg transition-colors active:scale-[0.98] disabled:opacity-30"
            >
              <ChevronDown className="h-4 w-4" />
            </button>

            {/* Interrupteur : la piste fait 24 × 44 px visuels, la zone de tap 44 × 44. */}
            <button
              type="button"
              role="switch"
              aria-checked={section.enabled}
              aria-label={`Afficher « ${section.label} »`}
              disabled={disabled}
              onClick={() => onToggle(section.id, !section.enabled)}
              className="flex h-11 w-14 shrink-0 items-center justify-center disabled:opacity-50"
            >
              <span
                className={cn(
                  'relative inline-flex h-6 w-11 rounded-full border-2 border-transparent transition-colors duration-200',
                  section.enabled ? 'bg-accent-400' : 'bg-ink-200',
                )}
              >
                <span
                  className={cn(
                    'pointer-events-none inline-block h-5 w-5 rounded-full bg-[#ffffff] shadow transition-transform duration-200',
                    section.enabled ? 'translate-x-5' : 'translate-x-0',
                  )}
                />
              </span>
            </button>
          </li>
        ))}
      </ul>

      {isCustomized && (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="min-h-11"
            onClick={onReset}
            disabled={disabled}
          >
            Réinitialiser
          </Button>
        </div>
      )}
    </div>
  );
}
